// The Trades screen (TRADES.md): offers for me, sent, under review, and
// history; a trade's detail beside the list on tablet and desktop and in a
// sheet on phone; the builder over everything. "Trade for" links arrive with
// the partner and player in the query string.
//
// THE MARKET BESIDE THE OFFERS (B-388): on a desktop the league's block,
// every roster's share of the top players at each position and the teams
// that fit mine are a column of their own, as TRADES.md lays the desktop
// out; on a tablet or a phone they are the Market segment. The screen opens
// on the first segment with something in it, not on an empty one.
//
// A CLEAN SCREEN, the first (eslint.config.js, CLEAN_SCREENS; LOOKS.md Part
// 7): drawn only through the kit, so a look restyles it from a stylesheet.
// One screen for the practice league and a real one: `useTrades` reads the
// simulator or `GET /leagues/:id/trades`, and `useTradeActions` sends what the
// manager does to the same place (PLAN-2026-09-26-trades T6).

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { TradeView, TradesView } from '@fsp/domain';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTrades } from '../data/hooks';
import { useTradeActions } from '../data/trades';
import { EmptyState, TeamMark } from '../kit';
import {
  Button,
  Chip,
  Grow,
  Line,
  ListRow,
  RowSub,
  Segments,
  Stack,
  Text,
  Title,
  tell,
} from '../kit/ui';
import { useTier } from '../shell/useTier';
import { DetailSheet } from '../templates/DetailSheet';
import { FromLeague } from './shared/FromLeague';
import { PlayerName, TeamName } from './shared/Names';
import { pickShort } from './shared/pickLabel';
import { MarkAvailableSheet } from './shared/TradeBlock';
import { TradeBuilder, type BuilderSeed } from './shared/TradeBuilder';
import { STATUS_LABEL, statusTone, TradeDetail } from './shared/TradeDetail';
import { TradesMarket } from './trades/TradesSide';
import '../styles/screens/trades.css';

type Segment = 'forMe' | 'sent' | 'review' | 'history' | 'block';

const EMPTY: Record<Exclude<Segment, 'block'>, string> = {
  forMe: 'No offers waiting on you. Go start one.',
  sent: 'Nothing out there. The builder is one tap away.',
  review: 'No trades under review.',
  history: 'No trades yet this season. Somebody has to be first.',
};

/** The first list with something in it, For me first: never an empty segment while another has trades. */
function firstSegment(view: TradesView): Exclude<Segment, 'block'> {
  for (const s of ['forMe', 'sent', 'review', 'history'] as const) if (view[s].length > 0) return s;
  return 'forMe';
}

export function TradesScreen() {
  return <FromLeague snapshot={useTrades()}>{(view) => <TradesBody view={view} />}</FromLeague>;
}

function TradesBody({ view }: { view: TradesView }) {
  const tier = useTier();
  const phone = tier === 'phone';
  // A desktop has room for the market beside the offers; a tablet or a
  // phone reaches it through its own segment.
  const wide = tier === 'desktop' || tier === 'large';
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [picked, setSegment] = useState<Segment>(() => firstSegment(view));
  const segment: Segment = wide && picked === 'block' ? firstSegment(view) : picked;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [builder, setBuilder] = useState<BuilderSeed | null>(null);
  const [marking, setMarking] = useState(false);
  const myBlock = useMemo(
    () =>
      view.block.filter((e) => e.isMine).map((e) => ({ playerId: e.player.id, wants: e.wants })),
    [view.block],
  );
  const actions = useTradeActions(view.league.id, view.myTeamId || null, myBlock);

  // A "Trade for" link or a Home button opens the builder or a trade straight away.
  useEffect(() => {
    if (params.get('new')) {
      setBuilder({
        open: true,
        partnerTeamId: params.get('with'),
        get: params.get('get') ? [params.get('get')!] : [],
        give: [],
        myDrops: [],
        counterOf: null,
      });
      setParams({}, { replace: true });
    } else if (params.get('trade')) {
      setSelectedId(params.get('trade'));
      setParams({}, { replace: true });
    } else if (params.get('tab') === 'block') {
      setSegment('block');
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const all = useMemo(() => [...view.forMe, ...view.sent, ...view.review, ...view.history], [view]);
  const selected = all.find((t) => t.id === selectedId) ?? null;
  const rows: TradeView[] = segment === 'block' ? [] : view[segment];
  // Keep the side column on the newest relevant trade when nothing is picked.
  const shown = selected ?? (phone ? null : (rows[0] ?? null));

  /** The builder, with a partner and one of theirs filled in when the door knew them. */
  const offer = (teamId: string | null, playerId?: string) =>
    setBuilder({
      open: true,
      partnerTeamId: teamId,
      give: [],
      get: playerId ? [playerId] : [],
      myDrops: [],
      counterOf: null,
    });

  const counter = (t: TradeView) => {
    const [proposer, receiver] = t.sides;
    setSelectedId(null);
    setBuilder({
      open: true,
      partnerTeamId: proposer.team.id,
      give: receiver.players.map((p) => p.player.id),
      get: proposer.players.map((p) => p.player.id),
      givePicks: [...receiver.picks],
      getPicks: [...proposer.picks],
      myDrops: [],
      counterOf: t.id,
    });
  };

  const saveBlock = async (entries: { playerId: string; wants: string }[], done: string) => {
    const outcome = await actions.setBlock(entries);
    if (!outcome.ok) return outcome.message;
    tell(done, 'good');
    return null;
  };

  const detail = (t: TradeView, onDone?: () => void) => (
    <TradeDetail key={t.id} trade={t} actions={actions} onCounter={counter} onDone={onDone} />
  );

  const segments = (
    <Segments
      label="Trades"
      value={segment}
      onChange={(s) => {
        setSegment(s);
        setSelectedId(null);
      }}
      items={[
        { id: 'forMe', label: 'For me', count: view.forMe.length, tone: 'warn' },
        { id: 'sent', label: 'Sent', count: view.sent.length },
        { id: 'review', label: 'Review', count: view.review.length },
        { id: 'history', label: 'History', count: view.history.length },
        ...(wide ? [] : [{ id: 'block' as const, label: 'Market', count: view.block.length }]),
      ]}
    />
  );

  const market = (
    <TradesMarket
      block={view.block}
      depth={view.depth}
      open={view.header.open}
      picksOpen={view.picksOpen}
      picks={view.picks}
      onOffer={offer}
      onMark={() => setMarking(true)}
      onRemove={(playerId) =>
        void saveBlock(
          myBlock.filter((e) => e.playerId !== playerId),
          'Off the block.',
        ).then((err) => (err ? tell(err, 'bad') : undefined))
      }
    />
  );

  const offers = (
    <div className="fsp-regions fsp-trades-regions">
      <section className="fsp-region" aria-label="Offers">
        {segment === 'block' ? null : rows.length === 0 ? (
          <EmptyState>{EMPTY[segment]}</EmptyState>
        ) : (
          rows.map((t) => {
            const [a, b] = t.sides;
            return (
              <ListRow
                key={t.id}
                lead={
                  <span className="fsp-trade-marks">
                    <TeamMark team={a.team} size={22} />
                    <TeamMark team={b.team} size={22} />
                  </span>
                }
                leadSize="lg"
                tall
                selected={shown?.id === t.id && !phone}
                onClick={() => setSelectedId(t.id)}
                cover="Open the trade"
                trail={<Chip tone={statusTone(t.status)}>{STATUS_LABEL[t.status]}</Chip>}
              >
                <Text block>
                  {[a, b].map((s, i) => (
                    <span key={s.team.id}>
                      {i === 1 ? ' · ' : null}
                      <Text weight="strong">
                        {s.team.isMine ? (
                          'You'
                        ) : (
                          <TeamName team={s.team}>{s.team.ownerName}</TeamName>
                        )}
                      </Text>
                      {s.team.isMine ? ' give ' : ' gives '}
                      {s.players.length === 0 && s.draftPicks.length === 0
                        ? 'nothing'
                        : s.players.map((p, j) => (
                            <Fragment key={p.player.id}>
                              {j > 0 ? ', ' : ''}
                              <PlayerName player={p.player} club={p.player.proTeam}>
                                {p.player.shortName}
                              </PlayerName>
                            </Fragment>
                          ))}
                      {s.draftPicks.map((p, j) => (
                        <Fragment key={p.id}>
                          {j > 0 || s.players.length > 0 ? ', ' : ''}
                          {pickShort(p)}
                        </Fragment>
                      ))}
                    </span>
                  ))}
                </Text>
                <RowSub>{t.timeLabel}</RowSub>
              </ListRow>
            );
          })
        )}
      </section>
      {phone ? null : (
        <section className="fsp-region fsp-trades-detail" aria-label="Trade">
          {shown ? detail(shown) : <EmptyState>Pick a trade, or start one.</EmptyState>}
        </section>
      )}
    </div>
  );

  return (
    <>
      <div className="fsp-screen fsp-flow">
        <div className="fsp-pinned">
          <div className="fsp-waivers-head">
            <Grow>
              <Stack gap={0}>
                <Title>Trades</Title>
                <Text tone="secondary" size="xs" block>
                  {view.header.closedReason ??
                    `${view.header.deadlineLabel}. ${view.header.reviewRule}`}
                </Text>
              </Stack>
            </Grow>
            <Line gap={2}>
              <Button
                variant="primary"
                size="xs"
                icon="add"
                disabled={!view.header.open}
                onClick={() => offer(null)}
              >
                New trade
              </Button>
              <Button
                variant="quiet"
                size="xs"
                onClick={() => navigate(`/l/${view.league.id}/rules`)}
              >
                Rules
              </Button>
            </Line>
          </div>
        </div>
        {wide ? (
          <div className={`fsp-duo fsp-tr-duo fsp-duo-${tier}`}>
            <div className="fsp-duo-main fsp-tr-main">
              {segments}
              {offers}
            </div>
            <div className="fsp-duo-side">{market}</div>
          </div>
        ) : (
          <>
            <div className="fsp-pinned fsp-pinned-stick">{segments}</div>
            {segment === 'block' ? (
              <div className="fsp-widgets fsp-tr-market">{market}</div>
            ) : (
              offers
            )}
          </>
        )}
      </div>
      {phone ? (
        <DetailSheet opened={selected !== null} onClose={() => setSelectedId(null)} title="Trade">
          {selected ? (
            detail(selected, () => setSelectedId(null))
          ) : (
            <Text tone="secondary">Gone.</Text>
          )}
        </DetailSheet>
      ) : null}
      <TradeBuilder seed={builder} actions={actions} onClose={() => setBuilder(null)} />
      <MarkAvailableSheet
        opened={marking}
        onClose={() => setMarking(false)}
        blocked={myBlock.map((e) => e.playerId)}
        onSave={(playerId, wants) =>
          saveBlock(
            [...myBlock.filter((e) => e.playerId !== playerId), { playerId, wants }],
            'On the block. The league can see him.',
          )
        }
      />
    </>
  );
}
